/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ChargingStationList,
  StationManagementInitialProperties,
} from '../../interface/station-management-side-panel.interface';
import stationManagement from '../../utils/api/station-management';

interface Charger {
  id: string;
  chargingPointName: string;
  isPersonalCharger: boolean;
  accountCustomId: string;
  accountName: string;
  chargingStationId: string;
  chargingStationName: string;
  chargingPointSn: string;
  
}

interface FetchChargingStationListArguments {
  submissionStatus: string;
}
// Async thunk
export const fetchChargingStationList = createAsyncThunk<
  ChargingStationList[],
  FetchChargingStationListArguments
>('station-management/fetchChargingStationList', async (data:FetchChargingStationListArguments, { dispatch }) => {
  try {
    const response = await stationManagement.fetchChargingStationList(data);
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationMessage',
      payload: {
        type: 'error',
        CONNREFUSED: error?.response?.data?.code || 200,
        message: error?.response?.data?.message || 'Something went wrong!',
        status: true,
      },
    });
    throw error;
  }
});

export const fetchCharingPointWithoutPagination = createAsyncThunk<Charger[], any>(
  'station-management/fetchCharingPointWithoutPagination',
  async (data, { dispatch }) => {
    try {
      const response = await stationManagement.fetchChargingPointWithoutPagination(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchAvgOnlineOfflineDetails = createAsyncThunk<Charger[], any>(
  'station-management/fetchAvgOnlineOfflineDetails',
  async (data, { dispatch }) => {
    try {
      const response = await stationManagement.fetchAvgOnlineOffline(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: StationManagementInitialProperties = {
  selectedChargingPoint: null,
  stationList: [],
  stationListLoading: false,
  chargingStationList: [],
  selectedStationPerformanceList: null,
  selectedSingleStationPerformanceList: null,
  onlineAvgHours: '0',
  offlineTotalHours: '0',
};
// Create the slice
export const chargingPointSlice = createSlice({
  name: 'device-management',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param state
     * @param action
     */
    setSelectedStation: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * Function to update selectedChargingPoint
     * @param state
     * @param action
     */
    setStationPerformance: (state, action) => {
      state.selectedStationPerformanceList = action.payload;
    },
    /**
     * Function to update selectedChargingPoint
     * @param state
     * @param action
     */
    setSingleStationPerformance: (state, action) => {
      state.selectedSingleStationPerformanceList = action.payload;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    // Extra reducer
    builder
      .addCase(fetchChargingStationList.pending, (state) => {
        state.stationListLoading = true;
      })
      .addCase(
        fetchChargingStationList.fulfilled,
        (state, action: PayloadAction<ChargingStationList[]>) => {
          state.chargingStationList = action.payload;
          state.stationListLoading = false;
        },
      )
      .addCase(fetchChargingStationList.rejected, (state) => {
        state.stationListLoading = false;
      });
    builder
      .addCase(fetchAvgOnlineOfflineDetails.pending, (state) => {
        state.stationListLoading = true;
      })
      .addCase(
        fetchAvgOnlineOfflineDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.onlineAvgHours = action.payload.onlineAvgHours;
          state.offlineTotalHours = action.payload.totalOfflineHours;
        },
      )
      .addCase(fetchAvgOnlineOfflineDetails.rejected, (state) => {
        state.stationListLoading = false;
      });
  },
});

// Export the actions
export const { setSelectedStation, setStationPerformance, setSingleStationPerformance } =
  chargingPointSlice.actions;
// Export the reducer
export default chargingPointSlice.reducer;
